import { type CSSProperties } from 'react';
import {
  EdgeLabelRenderer,
  SmoothStepEdge,
  StraightEdge,
  getSmoothStepPath,
  getStraightPath,
  type EdgeProps,
  type SmoothStepPathOptions,
} from '@xyflow/react';

import { isDocumentUnlinkEdge } from '../documentLinkValidation';
import { DOCUMENT_UNLINK_HINT } from '../documentUnlinkConfirmation';
import { useDocumentRefEdgeActions } from './documentRefEdgeActions';
import { UnlinkEdgeControl } from './UnlinkEdgeControl';

/**
 * HOF-219 — the selectable GS↔LS DOCUMENT reference edge.
 *
 * Before this, the dotted grey GS→LS arrow was a builtin ``smoothstep`` /
 * ``straight`` edge with an inline dashed style and nothing behind it. An
 * operator who could SEE that a Lieferschein had been grouped onto the wrong
 * Gutschrift had no way to say so — ``isManualLineEdge`` refused the edge for
 * the line-level unlink (correctly: it is not a line pair) and no other
 * affordance existed at any layer.
 *
 * Modelled on ``MatchedLineEdge`` deliberately, down to the delegation: the
 * geometry stays with the SAME builtin renderers ``buildDashedReferenceEdge``
 * would have used, with the same ``pathOptions``, so the carefully-tuned
 * #843/#1174 routing does not regress. What this component adds is (a) a
 * selection-aware stroke, because an inline ``style.stroke`` wins the cascade
 * over React Flow's ``.selected`` class and the pre-#1344 matched edges taught
 * exactly this lesson, (b) a wide invisible hit target for a 1 px dashed line,
 * and (c) the ``Verknüpfung lösen`` control at the path midpoint.
 *
 * Deliberately NOT wired to Backspace/Delete: that shortcut stays scoped to
 * line edges by ``isManualLineEdge`` (AC 14). A document sever can split a
 * group into two tiles and move money between them; it is not a keystroke.
 */

/** Geometry, identity and copy carried on the edge ``data``. ``buildDocumentRefEdge`` is the sole producer. */
export interface DocumentRefEdgeData {
  /** ``true`` → the straight co-linear router; ``false`` → cornered smoothstep. */
  coLinear: boolean;
  /** smoothstep bend options (only for the cornered geometry). */
  pathOptions?: SmoothStepPathOptions;
  settlementEventId: string;
  deliveryEventId: string;
  linkKind: string | null;
  groupId: string;
  settlementLabel: string;
  deliveryLabel: string;
  [key: string]: unknown;
}

/** Base (unselected) stroke for a dotted document reference edge — unchanged from #843. */
export const DOCUMENT_REF_BASE_STROKE = '#9ca3af';
export const DOCUMENT_REF_BASE_STROKE_WIDTH = 1;
/** Bolder + darker when selected, so the operator can SEE which pair is armed. */
export const DOCUMENT_REF_SELECTED_STROKE = '#4b5563';
export const DOCUMENT_REF_SELECTED_STROKE_WIDTH = 2.5;
/** Generous invisible hit target — the visible line is 1 px of dashes. */
export const DOCUMENT_REF_INTERACTION_WIDTH = 24;
const DASH_ARRAY = '4 3';

/**
 * Pure selection-aware style. Exported so the select→highlight contract is
 * unit-testable without mounting React Flow.
 *
 * The dash array is preserved in BOTH states on purpose: dashed is what tells
 * an operator this is a derived reference rather than a reconciled line pair,
 * and losing it on selection would make a selected document edge read as a
 * matched one.
 */
export function computeDocumentRefEdgeStyle(params: { selected: boolean }): CSSProperties {
  if (!params.selected) {
    return {
      stroke: DOCUMENT_REF_BASE_STROKE,
      strokeWidth: DOCUMENT_REF_BASE_STROKE_WIDTH,
      strokeDasharray: DASH_ARRAY,
    };
  }
  return {
    stroke: DOCUMENT_REF_SELECTED_STROKE,
    strokeWidth: DOCUMENT_REF_SELECTED_STROKE_WIDTH,
    strokeDasharray: DASH_ARRAY,
  };
}

/**
 * Does this edge paint the visible unlink control?
 *
 * Three conditions, and the third is the one that matters: ``isDocumentUnlinkEdge``
 * stays the SINGLE predicate deciding which edges carry a document unlink, for
 * the on-edge control and the context menu alike. ``hasActions`` false is a
 * Storybook / standalone / read-only mount, which shows no control at all
 * (AC 14) — the same gate ``MatchedLineEdge`` uses.
 *
 * Pure + exported so the matrix is unit-testable without mounting ReactFlow
 * (``EdgeLabelRenderer`` portals into a DOM node only a live ``<ReactFlow>``
 * provides, so a mounted assertion here would be a canvas test, not an edge
 * test).
 */
export function shouldRenderDocumentUnlinkControl(params: {
  selected: boolean;
  hasActions: boolean;
  data: unknown;
}): boolean {
  if (!params.hasActions || !params.selected) return false;
  return isDocumentUnlinkEdge({ data: params.data });
}

export function DocumentRefEdge(props: EdgeProps) {
  const data = (props.data ?? {}) as Partial<DocumentRefEdgeData>;
  const coLinear = data.coLinear === true;
  const style = computeDocumentRefEdgeStyle({ selected: !!props.selected });
  const actions = useDocumentRefEdgeActions();

  const showControl = shouldRenderDocumentUnlinkControl({
    selected: !!props.selected,
    hasActions: !!actions,
    data: props.data,
  });

  // Re-derive the label anchor from the SAME geometry helper the builtin
  // renderer below uses, rather than averaging the endpoints: on a cornered
  // smoothstep the midpoint of the straight line between the handles can sit
  // well off the drawn path, which would float the control over a card.
  const [, labelX, labelY] = coLinear
    ? getStraightPath({
        sourceX: props.sourceX,
        sourceY: props.sourceY,
        targetX: props.targetX,
        targetY: props.targetY,
      })
    : getSmoothStepPath({
        sourceX: props.sourceX,
        sourceY: props.sourceY,
        sourcePosition: props.sourcePosition,
        targetX: props.targetX,
        targetY: props.targetY,
        targetPosition: props.targetPosition,
        borderRadius: data.pathOptions?.borderRadius,
        offset: data.pathOptions?.offset,
        stepPosition: data.pathOptions?.stepPosition,
      });

  const control = showControl ? (
    <EdgeLabelRenderer>
      <div
        className="nodrag nopan"
        // ``EdgeLabelRenderer`` renders into an overlay with pointer events
        // off, so the control has to opt itself back in or it is decorative.
        style={{
          position: 'absolute',
          transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
          pointerEvents: 'all',
          zIndex: 10,
        }}
        data-testid={`document-unlink-edge-control-${props.id}`}
      >
        <UnlinkEdgeControl
          hint={DOCUMENT_UNLINK_HINT}
          onActivate={() => actions?.requestDocumentUnlink({ id: props.id, data: props.data }, 'edge_control')}
        />
      </div>
    </EdgeLabelRenderer>
  ) : null;

  if (coLinear) {
    return (
      <>
        <StraightEdge {...props} style={style} interactionWidth={DOCUMENT_REF_INTERACTION_WIDTH} />
        {control}
      </>
    );
  }
  return (
    <>
      <SmoothStepEdge
        {...props}
        style={style}
        pathOptions={data.pathOptions}
        interactionWidth={DOCUMENT_REF_INTERACTION_WIDTH}
      />
      {control}
    </>
  );
}

export default DocumentRefEdge;
